// "From seeds": the clips of every seed the producer has touched — uploaded,
// remixed from, downloaded, liked, jammed on — listed per seed, heard here,
// and KEPT into Mine with one tap, where they can be filed and tagged.
//
// Read-only beside Mine: a seed's clip is the seed's audio, so it is never
// moved or renamed here. Keeping takes a library reference to the same pool
// bytes (POST /api/library/clips/keep) — no transfer, no second copy.
// Presentation only; the page owns the player and the folder a kept clip
// lands in.

import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronRight, Loader2, Music, Pause, Play, Plus, Search, X } from "lucide-react";

import type { ReferenceClip, ReferenceHow, ReferenceLibraryResponse, ReferenceMidi, ReferenceSeed } from "@shared/api-types.js";
import { poolHashAnyChannel } from "@shared/blobs.js";
import type { ArrangementPlayerApi } from "@web-shared/hooks/useArrangementPlayer";
import { clipColor } from "@web-shared/lib/arrangement";
import { midiNotesToSource } from "@web-shared/lib/player-sources";
import { objectUrl } from "@web-shared/lib/api";
import type { ClipSource } from "@web-shared/lib/audio-engine";
import { relativeTime } from "@web-shared/lib/format";
import { cn } from "@web-shared/lib/utils";
import { webApi } from "@web-shared/lib/web-api";
import { seedPath } from "@/lib/ctas";
import { Avatar } from "./Avatar";
import { useSession } from "../session";

export const REFERENCE_KEY = ["web", "reference"] as const;

const HOW_LABEL: Record<ReferenceHow, string> = {
  uploaded: "Uploaded",
  remixed: "Remixed",
  downloaded: "Downloaded",
  liked: "Liked",
  jammed: "Jammed",
};
const HOWS: ReferenceHow[] = ["uploaded", "remixed", "downloaded", "liked", "jammed"];

/** The id a reference clip previews under. */
export function referenceClipId(seedId: string, audioKey: string): string {
  return `ref:${seedId}:${audioKey}`;
}

/** The id a seed's MIDI clip previews under. */
export function referenceMidiId(seedId: string, key: string): string {
  return `ref:${seedId}:midi:${key}`;
}

/** The source the ▶ plays: the object itself (same-origin, the way a room
 * fetches a seed's clip). A stem has no stated length; the file decides. */
export function referenceSource(seed: ReferenceSeed, clip: ReferenceClip): ClipSource {
  return {
    id: referenceClipId(seed.id, clip.audioKey),
    laneId: "reference",
    url: objectUrl(clip.audioKey),
    startSec: 0,
    ...(clip.durationBeats !== null && seed.bpm > 0 ? { durationSec: (clip.durationBeats * 60) / seed.bpm } : {}),
  };
}

/** "4 bars" / "1.5 beats" at the seed's tempo, or nothing for a stem. */
function lengthOf(clip: ReferenceClip): string | null {
  if (clip.durationBeats === null) return "full track";
  const bars = clip.durationBeats / 4;
  if (bars >= 1 && Number.isInteger(bars)) return `${bars} bar${bars === 1 ? "" : "s"}`;
  if (bars >= 1) return `${bars.toFixed(1)} bars`;
  return `${Number.isInteger(clip.durationBeats) ? clip.durationBeats : clip.durationBeats.toFixed(2)} beat${clip.durationBeats === 1 ? "" : "s"}`;
}

export function useReferenceLibrary() {
  const session = useSession();
  const userId = session?.user.id ?? null;
  return useQuery({
    queryKey: [...REFERENCE_KEY, userId ?? ""],
    enabled: userId !== null,
    queryFn: () => webApi<ReferenceLibraryResponse>("/me/reference"),
    // Signed URLs live two hours; the list changes when a seed is touched.
    staleTime: 10 * 60 * 1000,
  });
}

export function ReferenceClips({
  player,
  raw,
  onKeep,
  kept,
  keeping,
  intoName,
  onKeepMidi,
  keptMidi,
  keepingMidi,
  tempo,
}: {
  player: ArrangementPlayerApi;
  /** Previews play dry, from the top, once (the shelf's Raw). */
  raw: boolean;
  /** Keep this clip in Mine. The page sends it and reports back through `kept`. */
  onKeep: (seed: ReferenceSeed, clip: ReferenceClip) => void;
  /** Audio keys already in Mine (kept now, or there before). */
  kept: ReadonlySet<string>;
  /** The audio key being kept right now, if one is. */
  keeping: string | null;
  /** Where a kept clip lands: "Mine" or a folder's name. */
  intoName: string;
  /** Keep a seed's MIDI clip as an idea. Absent ⇒ MIDI rows are heard, not kept. */
  onKeepMidi?: (seed: ReferenceSeed, midi: ReferenceMidi) => void;
  /** MIDI reference ids (`referenceMidiId`) kept this visit. */
  keptMidi?: ReadonlySet<string>;
  keepingMidi?: string | null;
  /** The tempo MIDI previews play at (the page's preview tempo). */
  tempo?: number;
}) {
  const query = useReferenceLibrary();
  const [search, setSearch] = useState("");
  const [how, setHow] = useState<ReferenceHow | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set());

  const seeds = useMemo((): ReferenceSeed[] => {
    const all = query.data?.seeds ?? [];
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return all
      .filter((s) => how === null || s.how.includes(how))
      .map((s) => {
        if (words.length === 0) return s;
        const seedHit = words.every((w) => s.title.toLowerCase().includes(w) || s.producer.username.toLowerCase().includes(w));
        if (seedHit) return s;
        const clips = s.clips.filter((c) => words.every((w) => c.name.toLowerCase().includes(w) || c.trackName.toLowerCase().includes(w)));
        return clips.length > 0 ? { ...s, clips } : null;
      })
      .filter((s): s is ReferenceSeed => s !== null);
  }, [query.data, search, how]);
  const total = useMemo(() => (query.data?.seeds ?? []).reduce((n, s) => n + s.clips.length, 0), [query.data]);
  const shown = seeds.reduce((n, s) => n + s.clips.length, 0);
  const hows = useMemo(() => new Set((query.data?.seeds ?? []).flatMap((s) => s.how)), [query.data]);

  const previewMidi = (seed: ReferenceSeed, midi: ReferenceMidi): void => {
    const id = referenceMidiId(seed.id, midi.key);
    if (player.auditioningId === id) {
      player.stopAudition();
      return;
    }
    const bpm = tempo && tempo > 0 ? tempo : seed.bpm > 0 ? seed.bpm : 120;
    const seconds = (midi.durationBeats * 60) / bpm;
    const src = midiNotesToSource(midi.notes, bpm, { id, laneId: "reference", startSec: 0, durationSec: seconds });
    if (src) player.auditionMidiClip(src, { sync: !raw, loop: !raw && midi.durationBeats >= 4 });
  };
  const preview = (seed: ReferenceSeed, clip: ReferenceClip): void => {
    const src = referenceSource(seed, clip);
    if (player.auditioningId === src.id) {
      player.stopAudition();
      return;
    }
    const loop = clip.durationBeats !== null && clip.durationBeats >= 4;
    player.auditionClip(src, { sync: !raw, loop: !raw && loop });
  };
  const toggle = (id: string): void =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 pb-2">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a clip or a seed"
              aria-label="Find in seeds"
              autoComplete="off"
              className="h-9 w-full min-w-0 rounded-lg border border-panelline bg-control pl-7 pr-7 text-xs outline-none placeholder:text-muted-foreground/70 focus:border-controlline wide:h-7"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} aria-label="Clear search text" className="absolute right-1 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground wide:size-5">
                <X className="size-3" />
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="scrollbar-none flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-0.5" role="radiogroup" aria-label="How you came to a seed">
            {HOWS.filter((h) => hows.has(h)).map((h) => {
              const on = how === h;
              return (
                <button
                  key={h}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setHow(on ? null : h)}
                  className={cn(
                    "flex h-8 shrink-0 items-center rounded-full border px-2.5 text-[11px] font-semibold wide:h-6",
                    on ? "border-foreground/60 bg-white/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
                  )}
                >
                  {HOW_LABEL[h]}
                </button>
              );
            })}
          </div>
          <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-foreground" aria-live="polite">
            {search.trim() !== "" || how !== null ? `${shown} of ${total}` : `${seeds.length} seed${seeds.length === 1 ? "" : "s"} · ${total} clip${total === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto pb-3">
        {query.isPending ? (
          <p className="flex justify-center py-6">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </p>
        ) : query.isError ? (
          <p className="py-6 text-center text-xs text-removed">Your seeds couldn't be loaded. Try again in a minute.</p>
        ) : (query.data?.seeds.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-panelline px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">Nothing yet. The clips of every seed you upload, remix, download, like or jam on gather here.</p>
            <Link to="/discover" className="text-xs underline">
              Find a seed
            </Link>
          </div>
        ) : seeds.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Nothing matches.{" "}
            <button type="button" onClick={() => { setSearch(""); setHow(null); }} className="underline">
              Clear
            </button>
          </p>
        ) : (
          <ul role="list" aria-label="Seeds" className="flex flex-col gap-2">
            {seeds.map((seed) => {
              const open = !closed.has(seed.id);
              return (
                <li key={seed.id} className="rounded-xl border border-panelline bg-panel">
                  <div className="flex items-center gap-2 p-2">
                    <button
                      type="button"
                      onClick={() => toggle(seed.id)}
                      aria-expanded={open}
                      aria-label={`${open ? "Collapse" : "Expand"} ${seed.title}`}
                      className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-white/10 hover:text-foreground"
                    >
                      {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                    </button>
                    <Avatar name={seed.producer.username} url={seed.producer.avatarUrl} className="size-7 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold">
                        <Link to={seedPath(seed.id)} className="hover:underline" title="Open the seed">
                          {seed.title}
                        </Link>
                      </p>
                      <p className="flex flex-wrap items-center gap-x-2 text-[10px] text-muted-foreground">
                        <span className="truncate">{seed.producer.username}</span>
                        <span>{Math.round(seed.bpm * 10) / 10} BPM</span>
                        {seed.key && <span>{seed.key}</span>}
                        <span>
                          {seed.clips.length} clip{seed.clips.length === 1 ? "" : "s"}
                        </span>
                        <span title={new Date(seed.touchedAt).toLocaleString()}>{relativeTime(seed.touchedAt)}</span>
                      </p>
                    </div>
                    <span className="hidden shrink-0 items-center gap-1 sm:flex" aria-label="How you came to it">
                      {seed.how.map((h) => (
                        <span key={h} className="rounded bg-white/[0.07] px-1.5 text-[10px] leading-5 text-muted-foreground">
                          {HOW_LABEL[h]}
                        </span>
                      ))}
                    </span>
                  </div>
                  {open && (
                    <ul role="list" aria-label={`Clips of ${seed.title}`} className="flex flex-col gap-1 border-t border-panelline/60 p-2">
                      {seed.clips.length === 0 && (seed.midi?.length ?? 0) === 0 && <li className="px-2 py-1 text-[11px] text-muted-foreground">No clip audio on this seed.</li>}
                      {seed.clips.map((clip) => {
                        const id = referenceClipId(seed.id, clip.audioKey);
                        const playing = player.auditioningId === id;
                        const loading = player.auditionPendingId === id;
                        const inMine = kept.has(clip.audioKey);
                        const keepable = poolHashAnyChannel(clip.audioKey) !== null;
                        const color = clip.color !== null ? clipColor(clip.color, clip.trackIndex) : "#ffa529";
                        const length = lengthOf(clip);
                        return (
                          <li key={clip.audioKey} className={cn("flex min-h-12 items-center gap-2.5 rounded-lg border px-2 py-1 wide:min-h-10", playing ? "border-primary" : "border-transparent hover:border-panelline")}>
                            <button
                              type="button"
                              onClick={() => preview(seed, clip)}
                              title={playing ? "Stop" : "Preview"}
                              aria-label={`${playing ? "Stop" : "Preview"} ${clip.name}`}
                              className="relative flex size-9 shrink-0 items-center justify-center rounded-lg border text-black/80 hover:opacity-80 wide:size-7"
                              style={{ backgroundColor: color, borderColor: "rgba(0,0,0,.35)" }}
                            >
                              {loading ? <Loader2 className="size-4 animate-spin" /> : playing ? <Pause className="size-4" /> : <Play className="size-4" />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-semibold" title={clip.name}>
                                {clip.name}
                              </p>
                              <p className="flex flex-wrap items-center gap-x-2 text-[10px] text-muted-foreground">
                                <span className="truncate">{clip.trackName}</span>
                                {length && <span>{length}</span>}
                              </p>
                            </div>
                            {inMine ? (
                              <span className="flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] text-primary" title="In your library">
                                <Check className="size-3.5" /> In Mine
                              </span>
                            ) : keeping === clip.audioKey ? (
                              <Loader2 className="mx-2 size-3.5 shrink-0 animate-spin text-muted-foreground" />
                            ) : (
                              <button
                                type="button"
                                disabled={!keepable}
                                onClick={() => onKeep(seed, clip)}
                                title={keepable ? `Keep in ${intoName}` : "Audio from before the shared pool can't be kept — download the seed instead"}
                                aria-label={keepable ? `Keep ${clip.name} in ${intoName}` : undefined}
                                className="flex h-8 shrink-0 items-center gap-1 rounded-md border border-controlline bg-control px-2 text-[11px] font-semibold text-foreground/80 hover:bg-white/10 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 wide:h-7"
                              >
                                <Plus className="size-3.5" /> Keep
                              </button>
                            )}
                          </li>
                        );
                      })}
                      {(seed.midi ?? []).map((midi) => {
                        const id = referenceMidiId(seed.id, midi.key);
                        const playing = player.auditioningId === id;
                        const loading = player.auditionPendingId === id;
                        const color = clipColor(midi.color, midi.trackIndex);
                        const bars = midi.durationBeats / 4;
                        return (
                          <li key={id} className={cn("flex min-h-12 items-center gap-2.5 rounded-lg border px-2 py-1 wide:min-h-10", playing ? "border-primary" : "border-transparent hover:border-panelline")}>
                            <button
                              type="button"
                              onClick={() => previewMidi(seed, midi)}
                              title={playing ? "Stop" : "Preview (through the preview instrument)"}
                              aria-label={`${playing ? "Stop" : "Preview"} ${midi.name}`}
                              className="relative flex size-9 shrink-0 items-center justify-center rounded-lg border text-black/80 hover:opacity-80 wide:size-7"
                              style={{ backgroundColor: color, borderColor: "rgba(0,0,0,.35)" }}
                            >
                              {loading ? <Loader2 className="size-4 animate-spin" /> : playing ? <Pause className="size-4" /> : <Music className="size-4" />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-semibold" title={midi.name}>
                                {midi.name}
                              </p>
                              <p className="flex flex-wrap items-center gap-x-2 text-[10px] text-muted-foreground">
                                <span className="truncate">{midi.trackName}</span>
                                <span>MIDI</span>
                                <span>{Number.isInteger(bars) ? `${bars} bar${bars === 1 ? "" : "s"}` : `${midi.durationBeats} beats`}</span>
                                <span className="flex items-center gap-0.5">
                                  <Music className="size-2.5" />
                                  {midi.noteCount}
                                </span>
                              </p>
                            </div>
                            {keptMidi?.has(id) ? (
                              <span className="flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] text-primary" title="In your library">
                                <Check className="size-3.5" /> In Mine
                              </span>
                            ) : keepingMidi === id ? (
                              <Loader2 className="mx-2 size-3.5 shrink-0 animate-spin text-muted-foreground" />
                            ) : onKeepMidi ? (
                              <button
                                type="button"
                                onClick={() => onKeepMidi(seed, midi)}
                                title={`Keep in ${intoName} as a MIDI idea`}
                                aria-label={`Keep ${midi.name} in ${intoName}`}
                                className="flex h-8 shrink-0 items-center gap-1 rounded-md border border-controlline bg-control px-2 text-[11px] font-semibold text-foreground/80 hover:bg-white/10 hover:text-foreground wide:h-7"
                              >
                                <Plus className="size-3.5" /> Keep
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
            {query.data?.truncated && <li className="py-2 text-center text-[11px] text-muted-foreground">Showing the seeds you touched most recently — older ones were left out.</li>}
          </ul>
        )}
      </div>
    </div>
  );
}
